import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../utils/api";

type BonusConfig = {
  id: number;
  is_active: boolean;
  bonus_percentage: number;
  min_deposit_amount: number;
  award_mode: "each_deposit" | "every_7_days" | "all";
  cooldown_days: number;
  updated_at: string;
  valid_modes?: string[];
};

type BonusRecord = {
  id: number;
  user_id: number;
  user_name?: string | null;
  user_email?: string | null;
  source_table: string;
  source_ref: string;
  deposit_amount: number;
  currency: string;
  bonus_percentage: number;
  bonus_amount: number;
  status: string;
  reason: string | null;
  rule_mode: string;
  awarded_at: string | null;
  eligible_again_at: string | null;
  created_at: string;
};

type RecordsResponse = {
  total: number;
  page: number;
  limit: number;
  records: BonusRecord[];
};

const modeLabels: Record<string, string> = {
  each_deposit: "Each deposit",
  every_7_days: "Every 7 days",
  all: "All qualifying deposits"
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const formatAmount = (value?: number, currency = "INR") => {
  const amount = Number(value || 0);
  return `${currency} ${amount.toFixed(2)}`;
};

const DepositBonus: React.FC = () => {
  const [config, setConfig] = useState<BonusConfig | null>(null);
  const [form, setForm] = useState<BonusConfig | null>(null);
  const [records, setRecords] = useState<RecordsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    user_id: "",
    status: "",
    source_table: "",
    page: 1
  });

  const fetchConfig = async () => {
    const data = await apiFetch<BonusConfig>("/api/deposit-bonus/admin/config");
    setConfig(data);
    setForm(data);
  };

  const fetchRecords = async (page = filters.page) => {
    const params = new URLSearchParams();
    if (filters.user_id) params.set("user_id", filters.user_id);
    if (filters.status) params.set("status", filters.status);
    if (filters.source_table) params.set("source_table", filters.source_table);
    params.set("page", String(page));
    params.set("limit", "15");

    const data = await apiFetch<RecordsResponse>(`/api/deposit-bonus/admin/records?${params.toString()}`);
    setRecords(data);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchConfig(), fetchRecords(1)]);
      } catch (error: any) {
        setMessage(error.message || "Failed to load deposit bonus data.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const creditedSummary = useMemo(() => {
    const rows = records?.records || [];
    return rows.reduce(
      (acc, row) => {
        if (row.status === "credited") {
          acc.count += 1;
          acc.amount += Number(row.bonus_amount || 0);
        }
        return acc;
      },
      { count: 0, amount: 0 }
    );
  }, [records]);

  const updateForm = (field: keyof BonusConfig, value: string | boolean) => {
    if (!form) return;
    setForm({
      ...form,
      [field]:
        typeof value === "boolean"
          ? value
          : field === "award_mode"
          ? (value as BonusConfig["award_mode"])
          : Number(value)
    });
  };

  const saveConfig = async () => {
    if (!form) return;

    try {
      setSaving(true);
      setMessage(null);
      const saved = await apiFetch<BonusConfig>("/api/deposit-bonus/admin/config", {
        method: "PUT",
        body: JSON.stringify({
          is_active: form.is_active,
          bonus_percentage: Number(form.bonus_percentage),
          min_deposit_amount: Number(form.min_deposit_amount),
          award_mode: form.award_mode,
          cooldown_days: Number(form.cooldown_days)
        })
      });
      setConfig(saved);
      setForm(saved);
      setMessage("Deposit bonus settings saved.");
      await fetchRecords(1);
    } catch (error: any) {
      setMessage(error.message || "Failed to save deposit bonus settings.");
    } finally {
      setSaving(false);
    }
  };

  const applyFilters = async () => {
    try {
      setLoading(true);
      setMessage(null);
      await fetchRecords(1);
      setFilters((prev) => ({ ...prev, page: 1 }));
    } catch (error: any) {
      setMessage(error.message || "Failed to load bonus records.");
    } finally {
      setLoading(false);
    }
  };

  const goToPage = async (page: number) => {
    try {
      setLoading(true);
      await fetchRecords(page);
      setFilters((prev) => ({ ...prev, page }));
    } catch (error: any) {
      setMessage(error.message || "Failed to load bonus records.");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !form) {
    return <div className="min-h-screen bg-slate-950 text-white p-8 lg:ml-[250px]">Loading deposit bonus data...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6 lg:ml-[250px]">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-semibold">Deposit Bonus</h1>
            <p className="text-slate-400 mt-1">Configure the percentage, minimum deposit, and award mode for successful deposits.</p>
          </div>
          {message && (
            <div className="rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-200">
              {message}
            </div>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="text-sm text-slate-400">Recent credited bonuses</div>
            <div className="mt-2 text-3xl font-semibold">{creditedSummary.count}</div>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="text-sm text-slate-400">Recent bonus amount</div>
            <div className="mt-2 text-3xl font-semibold">{formatAmount(creditedSummary.amount)}</div>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="text-sm text-slate-400">Last updated</div>
            <div className="mt-2 text-lg font-medium">{formatDate(config?.updated_at)}</div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <label className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-950 px-4 py-3">
              <input
                type="checkbox"
                checked={!!form?.is_active}
                onChange={(event) => updateForm("is_active", event.target.checked)}
              />
              <span>Deposit bonus active</span>
            </label>

            <label className="block">
              <div className="mb-2 text-sm text-slate-400">Bonus percentage</div>
              <input
                type="number"
                min="0"
                value={form?.bonus_percentage ?? 0}
                onChange={(event) => updateForm("bonus_percentage", event.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none"
              />
            </label>

            <label className="block">
              <div className="mb-2 text-sm text-slate-400">Minimum deposit</div>
              <input
                type="number"
                min="0"
                value={form?.min_deposit_amount ?? 0}
                onChange={(event) => updateForm("min_deposit_amount", event.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none"
              />
            </label>

            <label className="block">
              <div className="mb-2 text-sm text-slate-400">Award mode</div>
              <select
                value={form?.award_mode ?? "each_deposit"}
                onChange={(event) => updateForm("award_mode", event.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none"
              >
                {(config?.valid_modes || ["each_deposit", "every_7_days", "all"]).map((mode) => (
                  <option key={mode} value={mode}>
                    {modeLabels[mode] || mode}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <div className="mb-2 text-sm text-slate-400">Cooldown days</div>
              <input
                type="number"
                min="0"
                value={form?.cooldown_days ?? 0}
                onChange={(event) => updateForm("cooldown_days", event.target.value)}
                disabled={form?.award_mode !== "every_7_days"}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none disabled:opacity-50"
              />
            </label>
          </div>

          <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-slate-400">
              `Each deposit` and `All qualifying deposits` both award against a fresh successful deposit. `Every 7 days` adds a cooldown before the next bonus can be awarded.
            </div>
            <button
              onClick={saveConfig}
              disabled={saving}
              className="rounded-xl bg-emerald-500 px-5 py-3 font-medium text-slate-950 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save settings"}
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <label className="block flex-1">
              <div className="mb-2 text-sm text-slate-400">User ID</div>
              <input
                type="number"
                value={filters.user_id}
                onChange={(event) => setFilters((prev) => ({ ...prev, user_id: event.target.value }))}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none"
              />
            </label>

            <label className="block flex-1">
              <div className="mb-2 text-sm text-slate-400">Status</div>
              <select
                value={filters.status}
                onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none"
              >
                <option value="">All statuses</option>
                <option value="credited">Credited</option>
                <option value="skipped">Skipped</option>
              </select>
            </label>

            <label className="block flex-1">
              <div className="mb-2 text-sm text-slate-400">Source table</div>
              <select
                value={filters.source_table}
                onChange={(event) => setFilters((prev) => ({ ...prev, source_table: event.target.value }))}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none"
              >
                <option value="">All sources</option>
                <option value="pay_in_transactions">pay_in_transactions</option>
                <option value="f2pay_orders">f2pay_orders</option>
                <option value="apaydeposits">apaydeposits</option>
              </select>
            </label>

            <button
              onClick={applyFilters}
              className="rounded-xl bg-sky-500 px-5 py-3 font-medium text-slate-950"
            >
              Apply filters
            </button>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-slate-400">
                <tr className="border-b border-slate-800">
                  <th className="px-3 py-3">User</th>
                  <th className="px-3 py-3">Source</th>
                  <th className="px-3 py-3">Deposit</th>
                  <th className="px-3 py-3">Bonus</th>
                  <th className="px-3 py-3">Mode</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {(records?.records || []).map((row) => (
                  <tr key={row.id} className="border-b border-slate-900 align-top">
                    <td className="px-3 py-4">
                      <div className="font-medium text-white">{row.user_name || `User ${row.user_id}`}</div>
                      <div className="text-slate-400">ID: {row.user_id}</div>
                      <div className="text-slate-500">{row.user_email || "-"}</div>
                    </td>
                    <td className="px-3 py-4">
                      <div className="font-medium">{row.source_table}</div>
                      <div className="text-slate-400 break-all">{row.source_ref}</div>
                    </td>
                    <td className="px-3 py-4">{formatAmount(row.deposit_amount, row.currency)}</td>
                    <td className="px-3 py-4">
                      <div>{formatAmount(row.bonus_amount, row.currency)}</div>
                      <div className="text-slate-400">{Number(row.bonus_percentage || 0).toFixed(2)}%</div>
                    </td>
                    <td className="px-3 py-4">{modeLabels[row.rule_mode] || row.rule_mode}</td>
                    <td className="px-3 py-4">
                      <div className={row.status === "credited" ? "text-emerald-400" : "text-amber-400"}>
                        {row.status}
                      </div>
                      <div className="text-slate-500">{row.reason || "-"}</div>
                    </td>
                    <td className="px-3 py-4">
                      <div>{formatDate(row.created_at)}</div>
                      <div className="text-slate-500">Awarded: {formatDate(row.awarded_at)}</div>
                    </td>
                  </tr>
                ))}
                {!records?.records?.length && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-slate-400">
                      No deposit bonus records found for the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {records && records.total > records.limit && (
            <div className="mt-5 flex items-center justify-between text-sm text-slate-400">
              <div>
                Showing page {records.page} of {Math.max(1, Math.ceil(records.total / records.limit))}
              </div>
              <div className="flex gap-2">
                <button
                  disabled={records.page <= 1}
                  onClick={() => goToPage(records.page - 1)}
                  className="rounded-lg border border-slate-700 px-4 py-2 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  disabled={records.page >= Math.ceil(records.total / records.limit)}
                  onClick={() => goToPage(records.page + 1)}
                  className="rounded-lg border border-slate-700 px-4 py-2 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepositBonus;
